import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, X } from 'lucide-react';
import Modal from '../common/modals/Modal';
import { useTheme } from '../../context/ThemeContext';
import { apiUpload, apiGet, apiPost } from '../../utils/api';
import toast from 'react-hot-toast';

const AuditImportModal = ({ isOpen, onClose, auditId, onImported }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // Step state: 'upload' | 'parsing' | 'review' | 'importing' | 'done'
  const [step, setStep] = useState('upload');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [importStats, setImportStats] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('upload');
      setSelectedFile(null);
      setTaskId(null);
      setParseResult(null);
      setImportStats(null);
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Select a file first');
      return;
    }

    setStep('parsing');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await apiUpload(`/admin/audit_tracker/audits/${auditId}/import`, formData);

      if (response.status === 202) {
        const data = await response.json();
        setTaskId(data.task_id);
        pollRef.current = setInterval(() => pollStatus(data.task_id), 2000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      toast.error(err.message);
      setStep('upload');
    }
  };

  const pollStatus = useCallback(async (tid) => {
    try {
      const response = await apiGet(`/admin/audit_tracker/audits/${auditId}/import_status/${tid}`);
      if (!response.ok) return;

      const data = await response.json();
      if (data.status === 'done') {
        clearInterval(pollRef.current);
        pollRef.current = null;

        if (data.result?.success) {
          setParseResult(data.result);
          setStep('review');
        } else {
          toast.error(data.result?.error || 'Parsing failed');
          setStep('upload');
        }
      }
    } catch {
      // Keep polling on network errors
    }
  }, [auditId]);

  const handleConfirm = async () => {
    if (!parseResult?.records) return;
    setStep('importing');

    try {
      const response = await apiPost(`/admin/audit_tracker/audits/${auditId}/import_confirm`, {
        records: parseResult.records,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Import failed');
      }

      const data = await response.json();
      setImportStats(data.stats);
      setStep('done');
      toast.success(data.message);
      if (onImported) onImported();
    } catch (err) {
      toast.error(err.message);
      setStep('review');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={step === 'parsing' || step === 'importing' ? undefined : onClose}
      title="Import Portfolio Tracker"
      size="2xl"
      footer={
        step === 'upload' ? (
          <>
            <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              Cancel
            </button>
            <button onClick={handleUpload} disabled={!selectedFile} className="px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              Upload & Parse
            </button>
          </>
        ) : step === 'review' ? (
          <>
            <button onClick={() => setStep('upload')} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              Back
            </button>
            <button onClick={handleConfirm} className="px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white">
              Confirm Import ({parseResult?.records_count} properties)
            </button>
          </>
        ) : step === 'done' ? (
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white">
            Done
          </button>
        ) : null
      }
    >
      <div className="min-h-[200px]">
        {/* Step 1: Upload workbook */}
        {step === 'upload' && (
          <div className="space-y-4">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Upload the Portfolio Tracker workbook. All sheets will be parsed automatically:
              Portfolio, Email Mapping, Production, Incoming Files, and Final Audit Year.
            </p>

            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const file = e.dataTransfer.files?.[0];
                if (file) setSelectedFile(file);
              }}
              className={`flex items-center justify-center gap-3 p-8 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                dragging
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {selectedFile ? (
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={20} className="text-green-500" />
                  <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{selectedFile.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                    className="p-1 text-red-400 hover:text-red-500"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload size={28} className={`mx-auto mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {dragging ? 'Drop file here' : 'Drag & drop or click to select .xlsx workbook'}
                  </span>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
          </div>
        )}

        {/* Step 2: Parsing */}
        {step === 'parsing' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-blue-500 mb-4" />
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Parsing {selectedFile?.name}...
            </p>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Reading all sheets, mapping columns, extracting and merging data
            </p>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 'review' && parseResult && (
          <div className="space-y-4">
            {/* Summary */}
            <div className={`flex items-center gap-2 p-3 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
              <CheckCircle2 size={18} className="text-green-500" />
              <span className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                Found {parseResult.records_count} properties
              </span>
            </div>

            {/* Sheets parsed */}
            {parseResult.sheets_parsed && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(parseResult.sheets_parsed).map(([sheet, info]) => (
                  <span
                    key={sheet}
                    className={`text-xs px-2 py-1 rounded ${
                      info.status === 'ok'
                        ? (isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700')
                        : (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')
                    }`}
                  >
                    {sheet.replace(/_/g, ' ')}: {info.status === 'ok' ? `${info.rows_extracted} rows` : info.error}
                  </span>
                ))}
              </div>
            )}

            {/* Property preview table */}
            <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className={`border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                    {['Property', 'State', 'Month', 'Status', 'Required', 'Received', 'Completed', 'Email'].map((h) => (
                      <th key={h} className={`px-2 py-1.5 text-left font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parseResult.records?.map((rec, i) => {
                    const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    return (
                      <tr key={i} className={`border-b ${isDark ? 'border-gray-700/50' : 'border-gray-100'}`}>
                        <td className={`px-2 py-1.5 font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{rec.property_name}</td>
                        <td className={`px-2 py-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rec.state || '-'}</td>
                        <td className={`px-2 py-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rec.audit_month ? monthNames[rec.audit_month] : '-'}</td>
                        <td className={`px-2 py-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {rec.status ? rec.status.replace(/_/g, ' ') : '-'}
                        </td>
                        <td className={`px-2 py-1.5 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rec.files_required ?? '-'}</td>
                        <td className={`px-2 py-1.5 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rec.files_received ?? '-'}</td>
                        <td className={`px-2 py-1.5 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rec.files_completed ?? '-'}</td>
                        <td className={`px-2 py-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate max-w-[150px]`}>{rec.contact_email || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Step 4: Importing */}
        {step === 'importing' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-blue-500 mb-4" />
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Importing {parseResult?.records_count} properties...
            </p>
          </div>
        )}

        {/* Step 5: Done */}
        {step === 'done' && importStats && (
          <div className="space-y-3 py-4">
            <div className={`flex items-center gap-2 p-3 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
              <CheckCircle2 size={18} className="text-green-500" />
              <span className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>Import complete</span>
            </div>
            <div className={`grid grid-cols-3 gap-4 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className="text-center">
                <div className="text-xl font-bold text-green-500">{importStats.created}</div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Created</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-blue-500">{importStats.updated}</div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Updated</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-400">{importStats.skipped}</div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Skipped</div>
              </div>
            </div>
            {importStats.errors?.length > 0 && (
              <div className={`p-3 rounded-lg ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={14} className="text-red-500" />
                  <span className={`text-xs font-medium ${isDark ? 'text-red-300' : 'text-red-700'}`}>{importStats.errors.length} errors</span>
                </div>
                {importStats.errors.slice(0, 5).map((err, i) => (
                  <div key={i} className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>{err}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AuditImportModal;
